// ── Name / identity ────────────────────────────────────────────────────────

/** The one IndexedDB. Keep the old name, thus the auth data stays. */
export const SHARED_DB_NAME = "lokasi-intelligence";

/** The object store in the DB. The old name keeps redux-persist compatible. */
export const STORE_NAME = "lokasi_intelligence_store";

/**
 * Marks a key for one tab: `persist:map` -> `persist:tab:<id>:map`.
 * The `:` on the two sides of the id keeps the id "abc" different from "abc123".
 */
export const TAB_KEY_INFIX = ":tab:";

/** The list of the live tabs. This list shows which tabs exist. */
export const TAB_REGISTRY_KEY = "__tabRegistry__";

/** Shows a change of the flag between two starts of the app. */
export const STORAGE_MODE_KEY = "__storageMode__";

/** Single-writer migration lock. */
export const MIGRATION_LOCK_KEY = "__storageMigrationLock__";

/** Stays in the shared space, thus the login applies to each tab. */
export const AUTH_PERSIST_KEY = "persist:auth";

/** The React Query cache. The tabs share it. The key is the persister key. */
export const REACT_QUERY_PERSIST_KEY = "react-query";

/** Keys with no tab prefix and no migration. `__*` keys are also shared. */
export const SHARED_PRESERVED_KEYS: ReadonlySet<string> = new Set([
  AUTH_PERSIST_KEY,
  REACT_QUERY_PERSIST_KEY
]);

/** The name of the BroadcastChannel for the PING and PONG messages. */
export const PRESENCE_CHANNEL_NAME = "lokasi:tab-presence";

// ── Timing ─────────────────────────────────────────────────────────────────

/** The interval of the "alive" mark of a tab. Keep it much less than STALE_GRACE_MS. */
export const KEEP_ALIVE_INTERVAL_MS = 30_000; // 30s

/**
 * The idle time before the cleanup can remove the keys of a tab. The value is
 * much more than the background freeze of the browser of approximately 5 min. A
 * frozen tab is alive, but it sends no keep-alive mark and no PONG. Thus do not
 * use a value less than approximately 10 min.
 */
export const STALE_GRACE_MS = 15 * 60_000; // 15 min

/** Send the PING more than one time. A busy tab can miss one PING. */
export const PRESENCE_PING_ROUNDS = 3;

/** The delay between two PING rounds. */
export const PRESENCE_PING_INTERVAL_MS = 400;

/** The time to collect the PONGs. It covers the rounds at 0, 400 and 800 ms. */
export const PRESENCE_TIMEOUT_MS = 1500;

/**
 * The probe for a duplicate tab. The time is less than for the cleanup check.
 * The probe runs at the start of the app, and the other tab was active before.
 */
export const CLAIM_PROBE_ROUNDS = 3;

/** Delay between claim-probe rounds. */
export const CLAIM_PROBE_INTERVAL_MS = 120;

/** The maximum time for the claim probe. It covers the rounds at 0, 120 and 240 ms. */
export const CLAIM_PROBE_TIMEOUT_MS = 500;

/** The interval of the cleanup. With STALE_GRACE_MS, a dead tab goes in 15 to 20 min. */
export const BACKGROUND_CLEANUP_INTERVAL_MS = 5 * 60_000; // 5 min

/** The minimum time between two cleanups that start when a tab becomes visible. */
export const VISIBILITY_SWEEP_THROTTLE_MS = 60_000; // 60s

/** The requestIdleCallback timeout for the first cleanup after the start. */
export const IDLE_CLEANUP_TIMEOUT_MS = 3000;

/** The life of the migration lock. It is sufficient for a migration, and short for a recovery. */
export const MIGRATION_LOCK_TTL_MS = 30_000; // 30s
